import { useState, useEffect, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface OptionalSectionProps {
  id: string
  label: string
  defaultOpen?: boolean
  /** Override the default i18n 'Skip for now' text */
  skipLabel?: string
  children: ReactNode
}

const STORAGE_KEY_PREFIX = 'optional-section-'

function readStoredState(id: string): boolean | null {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${id}`)
    if (raw === 'open') return true
    if (raw === 'closed') return false
    return null
  } catch {
    return null
  }
}

function writeStoredState(id: string, open: boolean): void {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${id}`, open ? 'open' : 'closed')
  } catch {
    // Ignore storage errors (e.g. private browsing quota exceeded)
  }
}

export function OptionalSection({
  id,
  label,
  defaultOpen = false,
  skipLabel,
  children,
}: OptionalSectionProps) {
  const { t } = useTranslation('common')

  // Initialise from localStorage; fall back to defaultOpen
  const [open, setOpen] = useState<boolean>(() => {
    const stored = readStoredState(id)
    return stored !== null ? stored : defaultOpen
  })

  // Re-sync when id changes (e.g. navigating between forms)
  useEffect(() => {
    const stored = readStoredState(id)
    setOpen(stored !== null ? stored : defaultOpen)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  function toggle() {
    const next = !open
    setOpen(next)
    writeStoredState(id, next)
  }

  const resolvedSkipLabel = skipLabel ?? t('actions.skip_for_now')

  return (
    <div>
      {/* Header row: chevron + label on the left, skip hint on the right */}
      <div className="flex justify-between items-center">
        <button
          type="button"
          onClick={toggle}
          className="flex items-center gap-1.5 text-sm font-medium text-base-content/80 hover:text-base-content transition-colors"
          aria-expanded={open}
          aria-controls={`optional-section-body-${id}`}
        >
          <ChevronDown
            className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${open ? '' : '-rotate-90'}`}
            aria-hidden="true"
          />
          {label}
        </button>

        <span className="text-xs text-base-content/50 flex-shrink-0 ml-2">
          {resolvedSkipLabel}
        </span>
      </div>

      {/* Body: always rendered, display:none when collapsed to preserve form state */}
      <div
        id={`optional-section-body-${id}`}
        style={{ display: open ? 'block' : 'none' }}
      >
        <div className="mt-3">
          {children}
        </div>
      </div>
    </div>
  )
}
